/**
 * Global UI density preference (compact | normal | spacious).
 *
 * Applied as `data-density` on `<html>` so CSS custom properties remapping
 * control height / radius / chrome / table padding resolve without React.
 * Preference is persisted via `user-preferences` (see UserPreferencesContext).
 *
 * Keep this module free of React / userPreferences imports so preferences
 * can safely import the type helpers without a cycle.
 */

export type AppDensity = "compact" | "normal" | "spacious";

export const DEFAULT_APP_DENSITY: AppDensity = "normal";

export const APP_DENSITY_OPTIONS: { value: AppDensity; label: string }[] = [
  { value: "compact", label: "Compact" },
  { value: "normal", label: "Normal" },
  { value: "spacious", label: "Spacious" },
];

export const isAppDensity = (value: unknown): value is AppDensity =>
  value === "compact" || value === "normal" || value === "spacious";

/**
 * react-grid-layout [x, y] gutters. Scales the prior frozen `[16, 24]` baseline
 * with density (field-gap rhythm, +4px vertical bias for large widget stacks).
 */
export const DENSITY_GRID_MARGIN_PX: Record<AppDensity, readonly [number, number]> = {
  compact: [12, 16],
  normal: [16, 24],
  spacious: [24, 32],
};

/** Read current density from `<html data-density>` (FOUC script / preferences). */
export const readDensity = (): AppDensity => {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-density");
    if (isAppDensity(attr)) {
      return attr;
    }
  }
  return DEFAULT_APP_DENSITY;
};

/** Apply density to `<html>` (CSS token source). Safe outside React. */
export const applyDensityAttr = (density: AppDensity): void => {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-density", density);
  }
};

/** Landing / `@centric/data-table` row-density vocabulary. */
export type RowDensity = "compact" | "default" | "comfortable";

/** App density → package `rowDensity` (1:1 by name after C14 ladder align). */
export const toRowDensity = (density: AppDensity): RowDensity => {
  if (density === "compact") {
    return "compact";
  }
  if (density === "spacious") {
    return "comfortable";
  }
  return "default";
};
